import { useMemo } from "react"

import { DEBUG_ENABLED } from "../debug/debugFlag"
import { DEFAULT_PARAMS, buildSnapshot } from "../debug/scenarios"
import type { DebugParams } from "../debug/scenarios"
import type { TvRoom } from "./useTvRoom"

/**
 * Drive the TV off the debug harness's fixtures instead of a socket.
 *
 * The same `{ phase, common, view }` triple the phone harness injects is exactly
 * what a display consumes, so reviewing all twelve screens costs nothing beyond
 * reading it off the URL: `/tv?demo=results&players=6&round=3`. That matters
 * more here than on the phone — half these screens only appear for a few seconds
 * in the middle of a real game, and several need eight people in the room.
 *
 * Available in dev always, and in a production build only behind the same
 * `mq:debug` flag the phone harness uses.
 */

const ENABLED = import.meta.env.DEV || DEBUG_ENABLED

export interface DemoParams {
    phase: DebugParams["phase"]
    players: number
    round: number
    stage?: string
}

/** The demo request encoded in the URL, or null when there isn't one. */
export function demoParams(): DemoParams | null {
    if (!ENABLED || typeof window === "undefined") return null

    const params = new URLSearchParams(window.location.search)
    const phase = params.get("demo")
    if (!phase) return null

    const int = (key: string, fallback: number) => {
        const raw = Number(params.get(key))
        return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : fallback
    }

    return {
        phase: phase as DebugParams["phase"],
        players: int("players", 6),
        round: int("round", 2),
        stage: params.get("stage") ?? undefined,
    }
}

/** A [TvRoom] built from fixtures, or null when no demo was requested. */
export function useTvDemo(): TvRoom | null {
    const request = demoParams()
    const key = request ? `${request.phase}:${request.players}:${request.round}:${request.stage}` : null

    return useMemo(() => {
        if (!request) return null

        const params: DebugParams = {
            ...DEFAULT_PARAMS,
            phase: request.phase,
            playerCount: request.players,
            round: request.round,
            stage: request.stage ?? DEFAULT_PARAMS.stage,
        }
        const snapshot = buildSnapshot(params)

        return {
            status: { kind: "watching" },
            // The wire sends GamePhaseType names; the fixtures speak the client's
            // lower-case phase. Match the wire so the TV's own phase checks —
            // round intro, accumulation — behave exactly as they will in a game.
            phase: snapshot.phase.toUpperCase(),
            room: snapshot.common,
            view: snapshot.view,
        }
        // `request` is rebuilt each render from the URL; the key is its identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key])
}
